import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { transcriptKeys } from "./queryKeys";
import {
	deleteTranscript,
	generateInsights,
	generateSummary,
	linkCandidate,
	linkEntity,
	syncTranscripts,
	unlinkCandidate,
	unlinkEntity,
	uploadTranscript,
} from "./api";
import type {
	LinkedCandidate,
	Transcript,
	TranscriptEntityType,
	TranscriptListItem,
	UploadTranscriptPayload,
} from "@/types/transcript";

// All transcript toasts live in these mutations; query errors render
// EmptyState + retry in the components instead of toasting.

type PatchFn = (transcript: Transcript) => Transcript;

/**
 * Apply the same optimistic patch to the detail cache and the matching row of
 * every transcript list cache, returning snapshots for rollback.
 */
function patchTranscriptCaches(
	queryClient: ReturnType<typeof useQueryClient>,
	transcriptId: string,
	patch: PatchFn
) {
	const snapshots: Array<{ queryKey: readonly unknown[]; data: unknown }> = [];

	const detailKey = transcriptKeys.detail(transcriptId);
	const detail = queryClient.getQueryData<Transcript>(detailKey);
	if (detail) {
		snapshots.push({ queryKey: detailKey, data: detail });
		queryClient.setQueryData(detailKey, patch(detail));
	}

	// Both the org-wide list and any per-candidate lists share the row shape.
	const listQueries = [
		...queryClient.getQueriesData<TranscriptListItem[]>({ queryKey: transcriptKeys.list() }),
		...queryClient.getQueriesData<TranscriptListItem[]>({ queryKey: [...transcriptKeys.all, "candidate"] }),
	];
	for (const [queryKey, rows] of listQueries) {
		if (!rows?.some((row) => row.id === transcriptId))
			continue;
		snapshots.push({ queryKey, data: rows });
		queryClient.setQueryData(
			queryKey,
			rows.map((row) => (row.id === transcriptId ? patch(row as Transcript) : row))
		);
	}

	return () => {
		for (const { queryKey, data } of snapshots)
			queryClient.setQueryData(queryKey, data);
	};
}

export function useSyncTranscripts() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: syncTranscripts,
		onSuccess: (result) => {
			toast({
				title: t("common.success", "Success"),
				description: t("transcripts.syncResult", "{{fetched}} transcripts fetched, {{linked}} candidates linked", {
					fetched: result.fetched,
					linked: result.linked,
				}),
			});
			queryClient.invalidateQueries({ queryKey: transcriptKeys.all });
		},
		onError: (error: Error) => {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.syncError", "Failed to sync transcripts."),
				variant: "destructive",
			});
		},
	});
}

export function useLinkCandidate() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ transcriptId, candidate }: { transcriptId: string; candidate: LinkedCandidate }) =>
			linkCandidate(transcriptId, candidate.id),
		onMutate: ({ transcriptId, candidate }) =>
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => ({
				...transcript,
				linked_candidates: transcript.linked_candidates.some((c) => c.id === candidate.id)
					? transcript.linked_candidates
					: [...transcript.linked_candidates, candidate],
			})),
		onError: (error: Error, _vars, rollback) => {
			rollback?.();
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.linkError", "Failed to link candidate."),
				variant: "destructive",
			});
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: transcriptKeys.all }),
	});
}

export function useUnlinkCandidate() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ transcriptId, candidateId }: { transcriptId: string; candidateId: string }) =>
			unlinkCandidate(transcriptId, candidateId),
		onMutate: ({ transcriptId, candidateId }) =>
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => ({
				...transcript,
				linked_candidates: transcript.linked_candidates.filter((c) => c.id !== candidateId),
			})),
		onError: (error: Error, _vars, rollback) => {
			rollback?.();
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.unlinkError", "Failed to unlink candidate."),
				variant: "destructive",
			});
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: transcriptKeys.all }),
	});
}

export function useLinkEntity() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ transcriptId, entityType, entityId }: {
			transcriptId: string;
			entityType: TranscriptEntityType;
			entityId: string;
		}) => linkEntity(transcriptId, entityType, entityId),
		onMutate: ({ transcriptId, entityType, entityId }) =>
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => {
				const current = transcript.relations[entityType] || [];
				if (current.includes(entityId))
					return transcript;
				return {
					...transcript,
					relations: { ...transcript.relations, [entityType]: [...current, entityId] },
				};
			}),
		onError: (error: Error, _vars, rollback) => {
			rollback?.();
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.linkEntityError", "Failed to link entity."),
				variant: "destructive",
			});
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: transcriptKeys.all }),
	});
}

export function useUnlinkEntity() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: ({ transcriptId, entityType, entityId }: {
			transcriptId: string;
			entityType: TranscriptEntityType;
			entityId: string;
		}) => unlinkEntity(transcriptId, entityType, entityId),
		onMutate: ({ transcriptId, entityType, entityId }) =>
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => ({
				...transcript,
				relations: {
					...transcript.relations,
					[entityType]: (transcript.relations[entityType] || []).filter((id) => id !== entityId),
				},
			})),
		onError: (error: Error, _vars, rollback) => {
			rollback?.();
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.unlinkEntityError", "Failed to unlink entity."),
				variant: "destructive",
			});
		},
		onSettled: () => queryClient.invalidateQueries({ queryKey: transcriptKeys.all }),
	});
}

export function useGenerateSummary() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: (transcriptId: string) => generateSummary(transcriptId),
		onSuccess: (summary, transcriptId) => {
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => ({ ...transcript, summary }));
			toast({
				title: t("common.success", "Success"),
				description: t("transcripts.summaryGenerated", "Summary generated"),
			});
		},
		onError: (error: Error) => {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.summaryError", "Failed to generate summary."),
				variant: "destructive",
			});
		},
	});
}

export function useGenerateInsights() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: (transcriptId: string) => generateInsights(transcriptId),
		onSuccess: (insights, transcriptId) => {
			patchTranscriptCaches(queryClient, transcriptId, (transcript) => ({ ...transcript, insights }));
			toast({
				title: t("common.success", "Success"),
				description: t("transcripts.insightsGenerated", "Insights generated"),
			});
		},
		onError: (error: Error) => {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.insightsError", "Failed to generate insights."),
				variant: "destructive",
			});
		},
	});
}

export function useUploadTranscript() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: (payload: UploadTranscriptPayload) => uploadTranscript(payload),
		onSuccess: () => {
			toast({
				title: t("common.success", "Success"),
				description: t("transcripts.uploadSuccess", "Transcript uploaded"),
			});
			queryClient.invalidateQueries({ queryKey: transcriptKeys.all });
		},
		onError: (error: Error) => {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.uploadError", "Failed to upload transcript."),
				variant: "destructive",
			});
		},
	});
}

export function useDeleteTranscript() {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutation({
		mutationFn: (transcriptId: string) => deleteTranscript(transcriptId),
		onSuccess: (_data, transcriptId) => {
			queryClient.removeQueries({ queryKey: transcriptKeys.detail(transcriptId) });
			toast({
				title: t("common.success", "Success"),
				description: t("transcripts.deleteSuccess", "Transcript deleted"),
			});
			queryClient.invalidateQueries({ queryKey: transcriptKeys.all });
		},
		onError: (error: Error) => {
			toast({
				title: t("common.error", "Error"),
				description: error.message || t("transcripts.deleteError", "Failed to delete transcript."),
				variant: "destructive",
			});
		},
	});
}
